import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";

import {
  type Filter,
  filterSchema,
  isValueless,
  type NumericComparator,
  numericComparators,
  withComparator,
} from "@/shared/components/filters/FilterSchema";
import { NumericValueInput } from "@/shared/components/filters/valueInputs/NumericValueInput";
import { Button } from "@/shared/foundation/primitives/Button";

import styles from "./MetricFilterForm.module.css";
import { OperatorPicker } from "./OperatorPicker";

export interface MetricFilterOption {
  /** The metric's bare dotted path (e.g. "navigation.path_deviation"). */
  name: string;
  /** Unit of measure (e.g. "%", "ms") **/
  unit: string | null;
}

export interface MetricFilterFormProps {
  /** The selected metric (name + unit) */
  metric: MetricFilterOption;
  /** Fired on Apply with the committed `metric` filter. */
  onCommit: (filter: Filter) => void;
  /** Fired when the user clicks Cancel. */
  onCancel: () => void;
  /**
   * Seeds the form with an existing metric filter's comparator and values
   * (the chip-edit flow). Its `field` must equal `metric.name`. When absent, the
   * form starts blank with the GREATER_THAN default (the add flow).
   */
  initialFilter?: Filter;
  /** Submit button label. Defaults to `"Apply"`; the edit popover passes
   *  `"Update filter"`. */
  submitLabel?: string;
}

/**
 * Right pane of the metrics filter: a metrics-specific header (muted path prefix
 * + bold leaf, inline and wrap-capable) over the reused numeric operator picker
 * and value input.
 */
export function MetricFilterForm({
  metric,
  onCommit,
  onCancel,
  initialFilter,
  submitLabel = "Apply",
}: MetricFilterFormProps) {
  const form = useForm<Filter>({
    resolver: zodResolver(filterSchema),
    defaultValues: initialFilter ?? {
      type: "metric",
      field: metric.name,
      comparator: "GREATER_THAN",
      values: [],
      unit: metric.unit,
    },
    // Continuous validation so the Apply button reflects validity live as the
    // user fills the value input.
    mode: "onChange",
  });

  const filter = form.watch();
  // A typed number not yet added to an "is any of" list. It is added when
  // focus leaves the input — as it does for the Apply button — so it counts
  // toward applying before it's in `values`.
  const [hasValidDraft, setHasValidDraft] = React.useState(false);

  const setIncludeNull = (includeNull: boolean) =>
    form.setValue("includeNull", includeNull, {
      shouldValidate: true,
      shouldDirty: true,
    });
  // Clear all changes "Not set" with the values, in one call.
  const setValues = (next: Filter["values"], includeNull?: boolean) => {
    form.setValue("values", next, { shouldValidate: true, shouldDirty: true });
    if (includeNull !== undefined) {
      setIncludeNull(includeNull);
    }
  };

  const lastDot = metric.name.lastIndexOf(".");
  const pathPrefix = lastDot === -1 ? "" : metric.name.slice(0, lastDot + 1); // incl. trailing dot
  const leaf = lastDot === -1 ? metric.name : metric.name.slice(lastDot + 1);

  return (
    <form
      className={styles.panel}
      autoComplete="off"
      onSubmit={(event) => {
        void form.handleSubmit((values) => onCommit(values))(event);
      }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <h3 className={styles.title} title={metric.name}>
            {pathPrefix && <span className={styles.path}>{pathPrefix}</span>}
            <span className={styles.leaf}>{leaf}</span>
          </h3>
          {metric.unit && (
            <span className={styles.unit} title={`Unit: ${metric.unit}`}>
              {metric.unit}
            </span>
          )}
        </header>

        <section className={styles.section} aria-label="Operator">
          <h4 className={styles.sectionLabel}>Operator</h4>
          <Controller
            name="comparator"
            control={form.control}
            render={({ field }) => (
              <OperatorPicker
                fieldType="numeric"
                value={field.value}
                onChange={(next) => {
                  // Read before the comparator changes: what survives
                  // depends on the operator being left.
                  const current = form.getValues();
                  const nextFilter = withComparator(current, next);
                  // Leaving a list operator drops its "Not set" member,
                  // which no other operator can hold.
                  if (
                    "includeNull" in current &&
                    !("includeNull" in nextFilter)
                  ) {
                    form.unregister("includeNull");
                  }
                  field.onChange(next);
                  setValues(nextFilter.values);
                }}
                // Metric reuses the numeric operator set (see `fieldType` above).
                operators={numericComparators}
                // Numeric has no presets, so neither does metric.
                presets={[]}
              />
            )}
          />
        </section>

        {!isValueless(filter.comparator) && (
          <section className={styles.section} aria-label="Value">
            <h4 className={styles.sectionLabel}>Value</h4>
            {/* The metric variant is numeric-shaped, so its comparator/values are
                always numeric; narrow for the numeric input.

                TODO: The form type should narrow for us.
              */}
            <NumericValueInput
              comparator={filter.comparator as NumericComparator}
              value={filter.values as number[]}
              onChange={setValues}
              unit={metric.unit}
              // Any metric can be missing from a session, which is why the
              // picker offers "is not set" for every one.
              nullable
              includeNull={"includeNull" in filter && filter.includeNull}
              onIncludeNullChange={setIncludeNull}
              onDraftValidChange={setHasValidDraft}
            />
          </section>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={onCancel}
          className={styles.footerAction}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={!form.formState.isValid && !hasValidDraft}
          className={styles.footerAction}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
